/**
 * Flight School Course Data — PWA port.
 *
 * Source of truth: client-native/src/features/flight-school/lib/flightSchoolCourseData.ts
 * (DO NOT edit the iOS file).
 *
 * UI-only data for Flight School modules displayed in the courses list. This is
 * independent of the real course system and its per-module status is derived
 * from the user's local Flight School progress. The courses track injects the
 * resulting DisplayCourse (id === FLIGHT_SCHOOL_COURSE_ID) alongside real API
 * courses.
 */

import type { DisplayCourse } from "@/hooks/useCourses";
import {
  deriveFlightSchoolModuleStatuses,
  getAvailableFlightSchoolModuleCount,
  getCurrentFlightSchoolModuleId,
  hasCompletedFlightSchoolProgression,
  isLastAvailableFlightSchoolModule,
  resolveFlightSchoolCompleteCelebration,
  resolveFlightSchoolModuleCompleteTitle,
  type FlightSchoolCompletionContext,
  type FlightSchoolModule,
  type FlightSchoolModuleStatus,
} from "../../../../../shared_types/flight-school/lib/flightSchoolCourseData";

export type {
  FlightSchoolCompletionContext,
  FlightSchoolModule,
  FlightSchoolModuleStatus,
};

export const FLIGHT_SCHOOL_COURSE_ID = -1;

/**
 * Flight School modules to display in the course path.
 * Status (completed, current, locked) is determined by the user's progress.
 */
export const FLIGHT_SCHOOL_MODULES: FlightSchoolModule[] = [
  {
    id: "cognates-1",
    title: "Cognates 1",
    subtitle: "CIÓN & ANTE patterns",
    type: "cognate",
  },
  {
    id: "cognates-2",
    title: "Cognates 2",
    subtitle: "DAD & MENTE patterns",
    type: "cognate",
  },
  {
    id: "cognates-3",
    title: "Cognates Review",
    subtitle: "Master all patterns",
    type: "cognate",
  },
  {
    id: "basic-phrases-1",
    title: "Basic Phrases 1",
    subtitle: "La Fiesta",
    type: "cognate",
  },
];

/**
 * Get the total number of available modules (excluding coming soon).
 */
export function getAvailableModuleCount(
  modules: FlightSchoolModule[] = FLIGHT_SCHOOL_MODULES,
): number {
  return getAvailableFlightSchoolModuleCount(modules);
}

/**
 * Get the total number of modules (including coming soon).
 */
export function getTotalModuleCount(
  modules: FlightSchoolModule[] = FLIGHT_SCHOOL_MODULES,
): number {
  return modules.length;
}

/**
 * Check if there are new modules since last seen.
 */
export function hasNewModules(
  lastSeenCount: number,
  modules: FlightSchoolModule[] = FLIGHT_SCHOOL_MODULES,
): boolean {
  return getAvailableModuleCount(modules) > lastSeenCount;
}

export { isLastAvailableFlightSchoolModule };

export function isFlightSchoolCompleteCelebration(
  moduleId: string,
  context?: FlightSchoolModule[] | FlightSchoolCompletionContext,
): boolean {
  return resolveFlightSchoolCompleteCelebration(
    moduleId,
    FLIGHT_SCHOOL_MODULES,
    context,
  );
}

export function getFlightSchoolModuleCompleteTitle(
  moduleId: string,
  context?: FlightSchoolModule[] | FlightSchoolCompletionContext,
): string {
  return resolveFlightSchoolModuleCompleteTitle(
    moduleId,
    FLIGHT_SCHOOL_MODULES,
    context,
  );
}

/**
 * Whether the user has finished every available module.
 */
export function hasGraduated(
  completedModules: string[],
  modules: FlightSchoolModule[] = FLIGHT_SCHOOL_MODULES,
): boolean {
  return hasCompletedFlightSchoolProgression(modules, completedModules);
}

/**
 * Derive per-module status for the courses-list path.
 * The first incomplete, non-coming-soon module is `current`; later incomplete
 * modules are `locked`. Mirrors iOS useFlightSchoolCourse derivation.
 */
export function deriveModuleStatuses(
  completedModules: string[],
  modules: FlightSchoolModule[] = FLIGHT_SCHOOL_MODULES,
): Record<string, FlightSchoolModuleStatus> {
  return deriveFlightSchoolModuleStatuses(modules, completedModules);
}

/**
 * The id of the first non-completed available module (the one the "Continue"
 * CTA should open). Falls back to the first module when everything is done.
 */
export function getCurrentModuleId(
  completedModules: string[],
  modules: FlightSchoolModule[] = FLIGHT_SCHOOL_MODULES,
): string {
  return getCurrentFlightSchoolModuleId(modules, completedModules);
}

/**
 * Build a DisplayCourse representing Flight School so it can appear alongside
 * real API courses in the courses list. The courses track injects this.
 */
export function createFlightSchoolDisplayCourse(
  completedModules: string[],
  isCurrent: boolean,
  modules?: FlightSchoolModule[],
): DisplayCourse {
  const source = modules && modules.length > 0 ? modules : FLIGHT_SCHOOL_MODULES;
  const available = source.filter((m) => !m.isComingSoon);
  const completedCount = available.filter((m) =>
    completedModules.includes(m.id),
  ).length;
  const progress =
    available.length > 0
      ? Math.round((completedCount / available.length) * 100)
      : 0;

  return {
    id: FLIGHT_SCHOOL_COURSE_ID,
    courseId: FLIGHT_SCHOOL_COURSE_ID,
    name: "Flight School",
    title: "🎓 Parrot Flight School",
    description: "Learn cognate patterns to kickstart your Spanish",
    level: { id: 1, name: "Super Beginner", difficulty: 1 },
    icon: "🎓",
    progress,
    isCurrent,
    topic: "0",
    totalChapters: available.length,
  };
}
